import { Capacitor } from '@capacitor/core';
import { IonBackButton, IonButtons, IonContent, IonHeader, IonItem, IonItemDivider, IonLabel, IonList, IonSpinner, IonTitle, IonToggle, IonToolbar, useIonAlert } from '@ionic/react';
import { unwrapResult } from '@reduxjs/toolkit';
import { useState } from 'react';
import { enableGeolocationLogRef } from '../../App';
import { logTransaction } from '../../common/log';
import { setLocationLogLevel } from '../../common/mapsUtil';
import { LogType } from '../../models/log.model';
import { RouteNames } from '../../models/routes.model';
import { useLocation } from '../../providers/LocationProvider';
import { useToast } from '../../providers/ToastProvider';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { setAllowPermissionStoreAsync, setHasAskedPermissionStoreAsync } from '../../store/locationSlice';
import { clearTransactionLogStoreAsync, sendDebugLogEmailAsync } from '../../store/logSlice';
import { setEnableDebugLogState, setEnableGeolocationLogStoreAsync, setIsDarkModeStoreAsync } from '../../store/settingsSlice';
import { IonPageWrapper } from './SettingsPage.styles';

interface IComponentProps {}

const SettingsPage: React.FC<IComponentProps> = () => {
  const [isSendingDebugLog, setIsSendingDebugLog] = useState(false);
  const [platform] = useState(Capacitor.getPlatform());

  const {
    actions: { enableLocationTracking, disableLocationTracking },
  } = useLocation();
  const {
    actions: { errorToast, infoToast },
  } = useToast();
  const { isDarkMode, enableDebugLog, gpsServicesAvailable, gpsWatchStarting } = useAppSelector((state) => state.settings);
  const { hasAskedPermision } = useAppSelector((state) => state.location);
  const [presentAlert] = useIonAlert();
  const dispatch = useAppDispatch();

  const onDarkModeChange = async (checked: boolean) => {
    await dispatch(setIsDarkModeStoreAsync(checked));
  };

  const onEnableLocationChange = async (checked: boolean, hasAskedPermision: boolean) => {
    if (checked) {
      let disclosureRes = false;
      const platform = Capacitor.getPlatform();
      if (platform === 'web') {
        disclosureRes = true;
      } else {
        logTransaction(LogType.Info, `Prompting prominent disclosure`);
        disclosureRes = await promptProminentDisclosure();
        await dispatch(setHasAskedPermissionStoreAsync(true));
        await dispatch(setAllowPermissionStoreAsync(disclosureRes));
        logTransaction(LogType.Info, `Prominent disclosure result was ${disclosureRes}`);
      }

      if (disclosureRes) {
        logTransaction(LogType.Info, `Disclosure result was true, getting location services ready`);
        await enableLocationTracking();
      } else {
        await disableLocationTracking();
      }
    } else {
      await disableLocationTracking();
    }
  };

  const onGeolocationLoggingChange = async (checked: boolean) => {
    await dispatch(setEnableGeolocationLogStoreAsync(checked));
    setLocationLogLevel(checked);
  };

  const onDebugLoggingChange = async (checked: boolean) => {
    if (checked) {
      await presentAlert({
        header: 'Capture Debug Logs?',
        subHeader: 'Debug logging may cause performance issues, are you sure?',
        buttons: [
          {
            text: 'Cancel',
            role: 'cancel',
            cssClass: 'secondary-button',
            handler: (val) => {
              dispatch(setEnableDebugLogState(false));
            },
          },
          {
            text: 'Yes',
            cssClass: 'secondary-button',
            handler: (val) => {
              dispatch(setEnableDebugLogState(true));
            },
          },
        ],
      });
    } else {
      dispatch(setEnableDebugLogState(checked));
    }
  };

  const handleShareDebugLogButtonClick = async () => {
    setIsSendingDebugLog(true);

    try {
      const resultAction = await dispatch(sendDebugLogEmailAsync());
      unwrapResult(resultAction);

      dispatch(setEnableDebugLogState(false));
      infoToast('Logs sent to support');
    } catch (ex: any) {
      // Handle error
      logTransaction(LogType.Error, `Caught error sending debug email - error: ${ex?.message}`);
      errorToast('Failed to send logs to support');
    } finally {
      setIsSendingDebugLog(false);
    }
  };

  const onClearLogsButtonClick = async () => {
    await presentAlert({
      header: 'Clear Logs?',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
          cssClass: 'secondary-button',
        },
        {
          text: 'Yes',
          cssClass: 'secondary-button',
          handler: async (val) => {
            await dispatch(clearTransactionLogStoreAsync());
          },
        },
      ],
    });
  };

  const promptProminentDisclosure = async () => {
    const alertPromise = new Promise<boolean>(async (res) => {
      await presentAlert({
        header: '',
        subHeader: '',
        message: 'Tech Mobile collects location data to enable distance tracking even when the app is closed or not in use.',
        buttons: [
          {
            text: 'Deny',
            role: 'cancel',
          },
          {
            text: 'Accept',
            role: 'confirm',
          },
        ],
        onDidDismiss: ({ detail }) => {
          if (detail.role === 'confirm') {
            res(true);
          } else {
            res(false);
          }
        },
      });
    });
    return alertPromise;
  };

  return (
    <IonPageWrapper>
      <IonHeader color="lightest">
        <IonToolbar color="primary">
          <IonButtons slot="start">
            <IonBackButton defaultHref={RouteNames.dashboard} color="secondary" />
          </IonButtons>
          <IonTitle>Settings</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent color="lightest">
        <IonList>
          <IonItemDivider>
            <IonLabel>General</IonLabel>
          </IonItemDivider>
          <IonItem button detail lines="none" routerDirection="forward" routerLink={RouteNames.autoSync}>
            <IonLabel>Auto Sync</IonLabel>
          </IonItem>
          <IonItem lines="none" button detail={false} onClick={(e) => onDarkModeChange(!isDarkMode)}>
            <IonToggle color="primarylight" checked={isDarkMode}>
              Dark Mode
            </IonToggle>
          </IonItem>
          {platform !== 'web' && (
            <IonItem lines="none" button detail={false} onClick={(e) => onEnableLocationChange(!gpsServicesAvailable, hasAskedPermision)}>
              <IonLabel>Enable Location Services</IonLabel>
              <IonToggle color="primarylight" checked={gpsServicesAvailable || gpsWatchStarting} disabled={gpsWatchStarting}></IonToggle>
            </IonItem>
          )}
          <IonItem lines="none" button detail onClick={() => handleShareDebugLogButtonClick()} disabled={isSendingDebugLog}>
            <IonLabel>
              <IonLabel>Share Log with Support</IonLabel>
            </IonLabel>
            {isSendingDebugLog && <IonSpinner name="lines-small" color="secondary" />}
          </IonItem>
          <IonItemDivider>
            <IonLabel>Logging</IonLabel>
          </IonItemDivider>
          {platform !== 'web' && (
            <IonItem lines="none" button detail={false} onClick={(e) => onGeolocationLoggingChange(!enableGeolocationLogRef)}>
              <IonLabel>Geolocation Logs</IonLabel>
              <IonToggle color="primarylight" checked={enableGeolocationLogRef}></IonToggle>
            </IonItem>
          )}
          <IonItem lines="none" button detail={false} onClick={(e) => onDebugLoggingChange(!enableDebugLog)}>
            <IonToggle color="primarylight" checked={enableDebugLog}>
              Debug Logs
            </IonToggle>
          </IonItem>
          {enableDebugLog && (
            <IonItem lines="none">
              <IonLabel>
                <p>Capturing logs...</p>
              </IonLabel>
              <IonSpinner name="dots" color="secondary" />
            </IonItem>
          )}
          {enableDebugLog && (
            <IonItem button detail lines="none" onClick={() => onClearLogsButtonClick()} disabled={isSendingDebugLog}>
              <IonLabel>
                <IonLabel>Clear Log</IonLabel>
              </IonLabel>
              {isSendingDebugLog && <IonSpinner name="lines-small" color="secondary" />}
            </IonItem>
          )}
          {enableDebugLog && (
            <IonItem button detail lines="none" onClick={() => handleShareDebugLogButtonClick()} disabled={isSendingDebugLog}>
              <IonLabel>
                <IonLabel>Share Log with Support</IonLabel>
              </IonLabel>
              {isSendingDebugLog && <IonSpinner name="lines-small" color="secondary" />}
            </IonItem>
          )}
        </IonList>
      </IonContent>
    </IonPageWrapper>
  );
};

export default SettingsPage;
